import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IssueCommentType, type IIssue } from '@d-orca/shared/trusstor';
import { ImageThumbnail } from '../Hub/ImageThumbnail';
import styles from './CommentsList.module.css';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { formatSiteDateTime } from '@d-orca/shared/client/utils';

interface ICommentsListProps {
  comments: NonNullable<IIssue['comments']>;
  siteId: string;
  issueId: string;
  onImageClick: (attachmentId: string) => void;
}

export function CommentsList({
  comments,
  siteId,
  issueId,
  onImageClick,
}: ICommentsListProps) {
  // Comment stamps in the SITE's zone; nothing rendered until it resolves.
  const tz = useSelector(selectSiteTz);
  const { t, i18n } = useTranslation();
  const bottomRef = useRef<HTMLDivElement>(null);

  // Pin to the bottom whenever a comment is added (and on first paint).
  // scrollIntoView walks up to the nearest scrollable ancestor — that's the
  // .commentsScroll container in HubBody, not the <ul> itself.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [comments.length]);

  if (comments.length === 0) {
    return <div className={styles.empty}>{t('issues.action.noComments')}</div>;
  }
  // Chronological — oldest first, newest at the bottom (chat-style)
  const sorted = [...comments].sort((a, b) => {
    const da = a.dateCreated instanceof Date ? a.dateCreated.getTime() : new Date(a.dateCreated as unknown as string).getTime();
    const db = b.dateCreated instanceof Date ? b.dateCreated.getTime() : new Date(b.dateCreated as unknown as string).getTime();
    return da - db;
  });
  return (
    <ul className={styles.list}>
      {sorted.map((c) => {
        const dateMs = c.dateCreated instanceof Date ? c.dateCreated.getTime() : new Date(c.dateCreated as unknown as string).getTime();
        const isPending = c.commentId.startsWith('temp-');
        const isText = c.content?.type === IssueCommentType.TEXT;
        const isImage = c.content?.type === IssueCommentType.IMAGE;

        return (
          <li
            key={c.commentId}
            className={`${styles.item}${isPending ? ` ${styles.itemPending}` : ''}`}
          >
            <div className={styles.itemHeader}>
              <span>{c.sender?.name ?? c.sender?.username ?? '?'}</span>
              <span>{tz && Number.isFinite(dateMs) ? formatSiteDateTime(dateMs, tz, i18n.language) : ''}</span>
            </div>

            {isText && (
              <div className={styles.textContent}>{c.content.value}</div>
            )}

            {isImage && (
              <ImageThumbnail
                attachmentId={c.content.value}
                siteId={siteId}
                issueId={issueId}
                /*
                 * Optimistic image comments synthesised by useAddImageComment
                 * stash the local blob URL directly in content.value. Detect
                 * that here and pass it through as `localBlobUrl` so the
                 * thumbnail skips the remote fetch (which would 500 — the
                 * blob URL isn't a valid attachmentId).
                 */
                localBlobUrl={c.content.value.startsWith('blob:') ? c.content.value : undefined}
                onClick={onImageClick}
              />
            )}

            {!isText && !isImage && (
              <div className={styles.nonTextContent}>
                {c.content?.filename ?? t('issues.hub.unsupportedAttachment')}
              </div>
            )}
          </li>
        );
      })}
      <div ref={bottomRef} aria-hidden="true" />
    </ul>
  );
}
